
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { withoutAcePowers, mergePowerWithMockExtras, type PowerInfo } from '@/lib/powers-data';
import {
  applyFairtradePrices,
  applyFairtradePricesToList,
  fairtradeEntriesToMap,
  fetchFairtradePricesFromSource,
  normalizeFairtradeApiResponse,
  type FairtradePricesMap,
} from '@/lib/fairtrade-data';
import {
  applyCatalogApiOverrides,
  syncCatalogPowersFromApi,
} from '@/lib/powers-catalog-sync';
import {
  editorInputToPower,
  type PowerCmsRecord,
  type PowerEditorInput,
  type PowersCmsState,
} from '@/lib/powers-cms';
import { isPubliclyVisible, type CmsPublicationStatus } from '@/lib/cms-publication';
import * as powersService from '@/services/powers.service';
import { hasApiAuthToken, ApiError } from '@/services/api-client';
import { XAT_API } from '@/services/api-paths';
import { toast } from 'sonner';

type FairtradeState = {
  prices: FairtradePricesMap | null;
  updatedAt: string | null;
  loading: boolean;
};

type PowersCmsContextValue = {
  powers: PowerCmsRecord[];
  catalogPowers: PowerCmsRecord[];
  hydrated: boolean;
  catalogApiBySlug: Record<string, PowerInfo>;
  fairtrade: FairtradeState;
  addPower: (input: PowerEditorInput) => Promise<PowerCmsRecord>;
  updatePower: (slug: string, input: PowerEditorInput) => Promise<void>;
  setPowerPublicationStatus: (slug: string, status: CmsPublicationStatus) => void;
  deletePower: (slug: string) => void;
  getPowerBySlug: (slug: string) => PowerCmsRecord | undefined;
  resetToMockSeed: () => void;
};

const PowersCmsContext = createContext<PowersCmsContextValue | null>(null);

function nowIso() {
  return new Date().toISOString();
}

function syncPublishedPower(list: PowerCmsRecord[], power: PowerCmsRecord): PowerCmsRecord[] {
  const without = list.filter((item) => item.slug !== power.slug);
  if (!isPubliclyVisible(power.publicationStatus)) return without;
  return [...without, power];
}

function mergePublicPowersPreferNewer(
  remote: PowerCmsRecord[],
  local: PowerCmsRecord[]
): PowerCmsRecord[] {
  const bySlug = new Map(remote.map((p) => [p.slug.toLowerCase(), p]));
  for (const power of local) {
    const key = power.slug.toLowerCase();
    const current = bySlug.get(key);
    if (!current) {
      if (isPubliclyVisible(power.publicationStatus)) bySlug.set(key, power);
      continue;
    }
    const localTs = Date.parse(power.updatedAt) || 0;
    const remoteTs = Date.parse(current.updatedAt) || 0;
    if (localTs >= remoteTs) bySlug.set(key, power);
  }
  return Array.from(bySlug.values());
}

/**
 * Rotas que precisam do catálogo público enriquecido via /prices (Home + lista Powerpedia).
 * Detalhe de power (`/powers/:slug`) resolve um único power via powerinfo e NÃO dispara sync em massa.
 */
function needsPublicCatalog(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  return path === '/' || path === '/powers';
}

/** Fairtrade: Home, Powerpedia (lista + detalhe) e editor de powers no painel. */
function needsFairtradePrices(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/' || path === '/powers' || path.startsWith('/powers/')) return true;
  return pathname.startsWith('/painel/powers');
}

export function PowersCmsProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const isPainelRoute = location.pathname.startsWith('/painel');
  const loadPublicCatalog = needsPublicCatalog(location.pathname);
  const loadFairtrade = needsFairtradePrices(location.pathname);
  const [state, setState] = useState<PowersCmsState>({ powers: [] });
  const [publicPowers, setPublicPowers] = useState<PowerCmsRecord[]>([]);
  const [catalogHydrated, setCatalogHydrated] = useState(false);
  const [adminHydrated, setAdminHydrated] = useState(false);
  const adminReadyRef = useRef(false);
  const [catalogApiBySlug, setCatalogApiBySlug] = useState<Record<string, PowerInfo>>({});
  const [fairtrade, setFairtrade] = useState<FairtradeState>({
    prices: null,
    updatedAt: null,
    loading: false,
  });

  /** Painel precisa dos powers admin; home/powers precisa do catálogo público. */
  const hydrated = isPainelRoute ? adminHydrated : loadPublicCatalog ? catalogHydrated : true;

  useEffect(() => {
    if (!loadPublicCatalog) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const pub = await powersService.getPublicPowers();
        if (!cancelled) {
          setPublicPowers((prev) => mergePublicPowersPreferNewer(pub as PowerCmsRecord[], prev));
        }
      } catch {
        /* mantém catálogo local se a API falhar */
      } finally {
        if (!cancelled) setCatalogHydrated(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadPublicCatalog]);

  useEffect(() => {
    let cancelled = false;

    const loadAdmin = async () => {
      if (!isPainelRoute || !hasApiAuthToken()) {
        if (!cancelled) {
          adminReadyRef.current = true;
          setAdminHydrated(true);
        }
        return;
      }

      // Não desidratar em refetch (ex.: refresh JWT ao voltar à aba) —
      // isso desmontava editores e apagava alterações locais.
      if (!adminReadyRef.current && !cancelled) setAdminHydrated(false);

      try {
        const admin = await powersService.getAdminPowers();
        if (!cancelled) {
          setState((s) => ({
            powers: mergePublicPowersPreferNewer(admin as PowerCmsRecord[], s.powers),
          }));
        }
      } catch {
        /* mantém estado local */
      } finally {
        if (!cancelled) {
          adminReadyRef.current = true;
          setAdminHydrated(true);
        }
      }
    };

    void loadAdmin();

    const onAuthChange = () => {
      void loadAdmin();
    };

    window.addEventListener('xat-auth-changed', onAuthChange);
    return () => {
      cancelled = true;
      window.removeEventListener('xat-auth-changed', onAuthChange);
    };
  }, [isPainelRoute]);

  useEffect(() => {
    if (!loadFairtrade) {
      setFairtrade((prev) => ({ ...prev, loading: false }));
      return;
    }

    let cancelled = false;
    setFairtrade((prev) => ({ ...prev, loading: true }));

    (async () => {
      try {
        let prices: FairtradePricesMap | null = null;
        let updatedAt: string | null = null;

        try {
          const res = await fetch(XAT_API.fairtradePrices(), { cache: 'no-store' });
          if (res.ok) {
            const data = (await res.json()) as { prices?: unknown; updatedAt?: string };
            prices =
              normalizeFairtradeApiResponse(data) ?? normalizeFairtradeApiResponse(data.prices);
            updatedAt = data.updatedAt ?? null;
          }
        } catch {
          /* proxy Laravel indisponível */
        }

        if (!prices) {
          const entries = await fetchFairtradePricesFromSource();
          prices = fairtradeEntriesToMap(entries);
          updatedAt = new Date().toISOString();
        }

        if (cancelled || !prices) {
          if (!cancelled) setFairtrade((prev) => ({ ...prev, loading: false }));
          return;
        }
        setFairtrade({
          prices,
          updatedAt,
          loading: false,
        });
      } catch {
        if (!cancelled) {
          setFairtrade((prev) => ({ ...prev, loading: false }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadFairtrade]);

  useEffect(() => {
    if (!loadPublicCatalog || publicPowers.length === 0) return;

    const base = withoutAcePowers(
      publicPowers.filter((p) => isPubliclyVisible(p.publicationStatus))
    );
    const slugs = [...base].sort((a, b) => b.id - a.id).map((power) => power.slug);
    const controller = new AbortController();

    void syncCatalogPowersFromApi(
      slugs,
      (batch) => {
        if (controller.signal.aborted) return;
        setCatalogApiBySlug((prev) => ({ ...prev, ...batch }));
      },
      controller.signal
    );

    return () => controller.abort();
  }, [loadPublicCatalog, publicPowers]);

  const getPowerBySlug = useCallback(
    (slug: string) => {
      const key = slug.toLowerCase();
      return (
        state.powers.find((p) => p.slug === key) ??
        publicPowers.find((p) => p.slug === key)
      );
    },
    [state.powers, publicPowers]
  );

  const addPower = useCallback(async (input: PowerEditorInput) => {
    const slug = input.slug.trim().toLowerCase();
    const t = nowIso();
    const optimistic: PowerCmsRecord = {
      ...editorInputToPower(input),
      createdAt: t,
      updatedAt: t,
    };
    setState((s) => ({ powers: [...s.powers.filter((p) => p.slug !== slug), optimistic] }));
    setPublicPowers((prev) => syncPublishedPower(prev, optimistic));

    try {
      const saved = (await powersService.createPower(optimistic)) as PowerCmsRecord;
      setState((s) => ({
        powers: [...s.powers.filter((p) => p.slug !== slug && p.slug !== saved.slug), saved],
      }));
      setPublicPowers((prev) => syncPublishedPower(prev.filter((p) => p.slug !== slug), saved));
      return saved;
    } catch (err) {
      setState((s) => ({ powers: s.powers.filter((p) => p.slug !== slug) }));
      setPublicPowers((prev) => prev.filter((p) => p.slug !== slug));
      toast.error('Não foi possível criar o power', {
        description: err instanceof ApiError ? err.message : 'Tente novamente.',
      });
      throw err;
    }
  }, []);

  const updatePower = useCallback(async (slug: string, input: PowerEditorInput) => {
    const key = slug.toLowerCase();
    const nextSlug = input.slug.trim().toLowerCase();
    let snapshot: PowerCmsRecord | undefined;
    let optimisticRow: PowerCmsRecord | null = null;
    setState((s) => {
      const existing = s.powers.find((p) => p.slug === key);
      if (!existing) return s;
      snapshot = existing;
      const updated: PowerCmsRecord = {
        ...editorInputToPower(input, existing),
        createdAt: existing.createdAt,
        updatedAt: nowIso(),
      };
      optimisticRow = updated;
      const without = s.powers.filter((p) => p.slug !== key && p.slug !== nextSlug);
      return { powers: [...without, updated] };
    });
    if (optimisticRow) {
      setPublicPowers((prev) => syncPublishedPower(prev, optimisticRow!));
    }

    try {
      const saved = (await powersService.updatePower(key, editorInputToPower(input))) as PowerCmsRecord;
      setState((s) => ({
        powers: [...s.powers.filter((p) => p.slug !== key && p.slug !== saved.slug), saved],
      }));
      setPublicPowers((prev) => syncPublishedPower(prev, saved));
    } catch (err) {
      if (snapshot) {
        setState((s) => ({
          powers: [...s.powers.filter((p) => p.slug !== key && p.slug !== snapshot!.slug), snapshot!],
        }));
        setPublicPowers((prev) => syncPublishedPower(prev, snapshot!));
      }
      toast.error('Não foi possível guardar o power', {
        description: err instanceof ApiError ? err.message : 'Tente novamente.',
      });
      throw err;
    }
  }, []);

  const setPowerPublicationStatus = useCallback((slug: string, status: CmsPublicationStatus) => {
    const key = slug.toLowerCase();
    setState((s) => ({
      powers: s.powers.map((p) =>
        p.slug === key ? { ...p, publicationStatus: status, updatedAt: nowIso() } : p
      ),
    }));

    void powersService
      .updatePower(key, { publicationStatus: status })
      .then((saved) => {
        const row = saved as PowerCmsRecord;
        setState((s) => ({
          powers: s.powers.map((p) => (p.slug === key ? row : p)),
        }));
        setPublicPowers((prev) => syncPublishedPower(prev, row));
      })
      .catch(() => undefined);
  }, []);

  const deletePower = useCallback((slug: string) => {
    const key = slug.toLowerCase();
    setState((s) => ({ powers: s.powers.filter((p) => p.slug !== key) }));
    setPublicPowers((prev) => prev.filter((p) => p.slug !== key));

    void powersService.deletePower(key).catch(() => undefined);
  }, []);

  const resetToMockSeed = useCallback(() => {
    void powersService.getAdminPowers().then((admin) => {
      setState({ powers: admin as PowerCmsRecord[] });
    });
  }, []);

  const value = useMemo<PowersCmsContextValue>(
    () => ({
      powers: state.powers,
      catalogPowers: publicPowers,
      hydrated,
      catalogApiBySlug,
      fairtrade,
      addPower,
      updatePower,
      setPowerPublicationStatus,
      deletePower,
      getPowerBySlug,
      resetToMockSeed,
    }),
    [state.powers, publicPowers, hydrated, catalogApiBySlug, fairtrade, addPower, updatePower, setPowerPublicationStatus, deletePower, getPowerBySlug, resetToMockSeed]
  );

  return <PowersCmsContext.Provider value={value}>{children}</PowersCmsContext.Provider>;
}

export function usePowersCms() {
  const ctx = useContext(PowersCmsContext);
  if (!ctx) throw new Error('usePowersCms must be used within PowersCmsProvider');
  return ctx;
}

/** Catálogo público via API `/powers`. */
export function useCatalogPowers(): {
  powers: PowerInfo[];
  hydrated: boolean;
  fairtradeUpdatedAt: string | null;
} {
  const ctx = useContext(PowersCmsContext);

  const powers = useMemo(() => {
    if (!ctx?.hydrated) return [];

    let base = withoutAcePowers(
      ctx.catalogPowers.filter((p) => isPubliclyVisible(p.publicationStatus))
    ).map((power) => mergePowerWithMockExtras(power));

    const map = ctx.fairtrade.prices;
    if (Object.keys(ctx.catalogApiBySlug).length > 0) {
      base = applyCatalogApiOverrides(base, ctx.catalogApiBySlug);
    }

    if (map) {
      base = applyFairtradePricesToList(base, map);
    }

    return base;
  }, [ctx?.hydrated, ctx?.catalogPowers, ctx?.fairtrade.prices, ctx?.catalogApiBySlug]);

  return {
    powers,
    hydrated: ctx?.hydrated ?? false,
    fairtradeUpdatedAt: ctx?.fairtrade.updatedAt ?? null,
  };
}

export function useFairtradePrices(): FairtradeState {
  const ctx = useContext(PowersCmsContext);
  return ctx?.fairtrade ?? { prices: null, updatedAt: null, loading: true };
}

export function usePowerWithFairtradePrices(power: PowerInfo | null): PowerInfo | null {
  const { prices } = useFairtradePrices();

  return useMemo(() => {
    if (!power || !prices) return power;
    return applyFairtradePrices(power, prices);
  }, [power, prices]);
}
